import { FieldStructureMap } from '../../../service-request-form-response.mapper.util';

export const SERVICE_REQUEST_FORM_INSPECTION_CONTROL_KEYS = [
  'inspectionNo',
  'inspectionType',
  'inspectionDate',
  'inspectionOutcome',
  'outcomeRemarks',
] as const;

/** Follow-up-only controls that are hidden when evaluation follow-up is not needed. */
export const SERVICE_REQUEST_FORM_FOLLOWUP_EXCLUSIVE_CONTROL_KEYS = [
  'deficiency',
  'followupInspectionDate',
  'complianceTimeframe',
  'makingCorrections',
  'followupCorrectiveAction',
  'deficiencyCorrectedBy',
  'deficiencyCorrectedDate',
  'pictureOfRedTag',
  'correctionPhoto',
  'correctionComments',
  'followupTechnicianCertificateNumber',
  'signOffComments',
  'actionTaken',
  'technicianComments',
] as const;

export function omitInspectionFieldsFromStructure(structure: FieldStructureMap): FieldStructureMap {
  const omitted = new Set<string>(SERVICE_REQUEST_FORM_INSPECTION_CONTROL_KEYS);
  return Object.fromEntries(
    Object.entries(structure).filter(([key]) => !omitted.has(key)),
  );
}

export function resolveInspectionAwareFieldStructure(
  structure: FieldStructureMap,
  includeInspection: boolean,
): FieldStructureMap {
  return includeInspection ? structure : omitInspectionFieldsFromStructure(structure);
}

const field = (
  controlKey: string,
  label: string,
  sequence: number,
  overrides: Partial<FieldStructureMap[string]> = {},
): [string, FieldStructureMap[string]] => [
  controlKey,
  {
    type: overrides.type ?? 'TEXT',
    label,
    sequence,
    required: overrides.required ?? false,
    ...overrides,
  },
];

export const SHARED_GENERAL_INFO_LEFT_KEYS = [
  'serviceRequestNo',
  'serviceRequestType',
  'dateCreated',
  'createdBy',
] as const;

export const SHARED_GENERAL_INFO_RIGHT_KEYS = [
  'customerAccount',
  'shipToLocation',
  'shipToCoordinates',
  'shipToLastInspection',
  'lsd',
] as const;

export function buildGeneralInformationFieldStructure(startSequence = 2): FieldStructureMap {
  return Object.fromEntries([
    field('serviceRequestNo', 'Service Request No.', startSequence, { readOnly: true }),
    field('serviceRequestType', 'Service Request Type', startSequence + 1, { readOnly: true }),
    field('dateCreated', 'Date Created', startSequence + 2, { readOnly: true }),
    field('createdBy', 'Created By', startSequence + 3, { readOnly: true }),
    field('customerAccount', 'Customer Account', startSequence + 4, { readOnly: true }),
    field('shipToLocation', 'Ship-To Location', startSequence + 5, { readOnly: true }),
    field('shipToCoordinates', 'Ship-To Longitude, Latitude', startSequence + 6, { readOnly: true }),
    field('shipToLastInspection', 'Ship-To Last Inspection', startSequence + 7, { readOnly: true }),
    field('lsd', 'LSD (If Applicable)', startSequence + 8, { readOnly: true }),
  ]);
}

export function buildTechnicianSignatureFieldStructure(startSequence: number): FieldStructureMap {
  return Object.fromEntries([
    field('technicianName', 'Inspector', startSequence, { type: 'USER_LOOK_UP', required: true }),
    field('serviceDate', 'Date and Timestamp', startSequence + 1, { readOnly: true }),
    field('inspectorDesignation', 'Inspector Designation', startSequence + 2, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    field('locationLongitude', 'Location Longitude', startSequence + 3, { readOnly: true }),
    field('inspectorRotNumber', 'Inspector ROT Number', startSequence + 4, { type: 'TEXT_AREA', required: true, maxLength: 100 }),
    field('locationLatitude', 'Location Latitude', startSequence + 5, { readOnly: true }),
    field('rotExpiryDate', 'ROT Expiry Date', startSequence + 6, { type: 'DATE', required: true }),
    field('technicianSignature', 'Technician Signature', startSequence + 7, { type: 'IMAGE', required: true }),
  ]);
}

export function buildChecklistField(
  controlKey: string,
  label: string,
  sequence: number,
): [string, FieldStructureMap[string]] {
  return field(controlKey, label, sequence, { type: 'SINGLE', required: true, isChecklistItem: true });
}
